import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Download,
  Upload,
  Copy,
  Save,
  GripVertical
} from 'lucide-react';
import {
  apiService,
  SpeechToTextProvider,
  SttModel,
  SttModelConfigSchema,
  SttModelConfigField,
  SttConfigFieldOption,
  LocalizedText
} from '../../services/api';
import { copyToClipboard } from '../../utils/clipboard';

function getLocalizedText(text: LocalizedText | undefined, lang = 'de'): string {
  if (!text) return '';
  if (typeof text === 'string') return text;
  return text[lang] || text['en'] || Object.values(text)[0] || '';
}

// ===== Visual Config Editor types =====

interface ConfigFieldFormData {
  key: string;
  type: SttModelConfigField['type'];
  labelDe: string;
  labelEn: string;
  descriptionDe: string;
  descriptionEn: string;
  defaultValue: string;
  group: string;
  order: string;
  // Type-specific
  options: OptionFormData[];
  min: string;
  max: string;
  step: string;
  maxLength: string;
}

interface OptionFormData {
  value: string;
  labelDe: string;
  labelEn: string;
  hintDe: string;
  hintEn: string;
  group: string;
}

const FIELD_TYPES: { value: SttModelConfigField['type']; label: string }[] = [
  { value: 'string', label: 'Text (string)' },
  { value: 'textarea', label: 'Textbereich (textarea)' },
  { value: 'number', label: 'Zahl (number)' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'select', label: 'Auswahl (select)' },
  { value: 'slider', label: 'Schieberegler (slider)' },
  { value: 'list', label: 'Liste (list)' }
];

function emptyOption(): OptionFormData {
  return { value: '', labelDe: '', labelEn: '', hintDe: '', hintEn: '', group: '' };
}

function emptyField(): ConfigFieldFormData {
  return {
    key: '',
    type: 'string',
    labelDe: '',
    labelEn: '',
    descriptionDe: '',
    descriptionEn: '',
    defaultValue: '',
    group: '',
    order: '',
    options: [],
    min: '',
    max: '',
    step: '',
    maxLength: ''
  };
}

// ===== Conversion helpers =====

function configSchemaToFields(schema: SttModelConfigSchema | undefined): ConfigFieldFormData[] {
  if (!schema) return [];
  return Object.entries(schema)
    .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
    .map(([key, field]) => {
      const label = typeof field.label === 'string'
        ? { de: field.label, en: field.label }
        : { de: field.label?.['de'] || '', en: field.label?.['en'] || '' };
      const desc = !field.description ? { de: '', en: '' }
        : typeof field.description === 'string'
          ? { de: field.description, en: field.description }
          : { de: field.description?.['de'] || '', en: field.description?.['en'] || '' };

      const options: OptionFormData[] = (field.options || []).map(opt => {
        const optLabel = typeof opt.label === 'string'
          ? { de: opt.label, en: opt.label }
          : { de: opt.label?.['de'] || '', en: opt.label?.['en'] || '' };
        const optHint = !opt.hint ? { de: '', en: '' }
          : typeof opt.hint === 'string'
            ? { de: opt.hint, en: opt.hint }
            : { de: opt.hint?.['de'] || '', en: opt.hint?.['en'] || '' };
        return {
          value: String(opt.value),
          labelDe: optLabel.de,
          labelEn: optLabel.en,
          hintDe: optHint.de,
          hintEn: optHint.en,
          group: opt.group || ''
        };
      });

      return {
        key,
        type: field.type,
        labelDe: label.de,
        labelEn: label.en,
        descriptionDe: desc.de,
        descriptionEn: desc.en,
        defaultValue: field.default !== undefined
          ? (Array.isArray(field.default) ? field.default.join(', ') : String(field.default))
          : '',
        group: field.group || '',
        order: field.order !== undefined ? String(field.order) : '',
        options,
        min: field.min !== undefined ? String(field.min) : '',
        max: field.max !== undefined ? String(field.max) : '',
        step: field.step !== undefined ? String(field.step) : '',
        maxLength: field.maxLength !== undefined ? String(field.maxLength) : ''
      };
    });
}

function fieldsToConfigSchema(fields: ConfigFieldFormData[]): SttModelConfigSchema {
  const schema: SttModelConfigSchema = {};
  for (const f of fields) {
    if (!f.key.trim()) continue;

    const label: Record<string, string> = {};
    if (f.labelDe) label['de'] = f.labelDe;
    if (f.labelEn) label['en'] = f.labelEn;

    const configField: SttModelConfigField = {
      type: f.type,
      label: Object.keys(label).length > 0 ? label : f.key
    };

    const desc: Record<string, string> = {};
    if (f.descriptionDe) desc['de'] = f.descriptionDe;
    if (f.descriptionEn) desc['en'] = f.descriptionEn;
    if (Object.keys(desc).length > 0) configField.description = desc;

    if (f.defaultValue !== '') {
      if (f.type === 'boolean') {
        configField.default = f.defaultValue === 'true';
      } else if (f.type === 'number' || f.type === 'slider') {
        configField.default = Number(f.defaultValue);
      } else if (f.type === 'list') {
        configField.default = f.defaultValue.split(',').map(s => s.trim()).filter(s => s !== '');
      } else {
        configField.default = f.defaultValue;
      }
    }

    if (f.group) configField.group = f.group;
    if (f.order !== '') configField.order = Number(f.order);

    if (f.type === 'select' && f.options.length > 0) {
      configField.options = f.options
        .filter(o => o.value !== '')
        .map(o => {
          const optLabel: Record<string, string> = {};
          if (o.labelDe) optLabel['de'] = o.labelDe;
          if (o.labelEn) optLabel['en'] = o.labelEn;

          const opt: SttConfigFieldOption = {
            value: o.value,
            label: Object.keys(optLabel).length > 0 ? optLabel : o.value
          };

          const optHint: Record<string, string> = {};
          if (o.hintDe) optHint['de'] = o.hintDe;
          if (o.hintEn) optHint['en'] = o.hintEn;
          if (Object.keys(optHint).length > 0) opt.hint = optHint;
          if (o.group) opt.group = o.group;
          return opt;
        });
    }

    if ((f.type === 'number' || f.type === 'slider') && f.min !== '') configField.min = Number(f.min);
    if ((f.type === 'number' || f.type === 'slider') && f.max !== '') configField.max = Number(f.max);
    if ((f.type === 'number' || f.type === 'slider') && f.step !== '') configField.step = Number(f.step);
    if ((f.type === 'string' || f.type === 'textarea') && f.maxLength !== '') configField.maxLength = Number(f.maxLength);

    schema[f.key.trim()] = configField;
  }
  return schema;
}

function modelToBasicForm(model: SttModel) {
  const getField = (field: LocalizedText | undefined): { de: string; en: string } => {
    if (!field) return { de: '', en: '' };
    if (typeof field === 'string') return { de: field, en: field };
    return { de: field['de'] || '', en: field['en'] || '' };
  };
  const displayName = getField(model.displayName);
  const description = getField(model.description);
  return {
    displayNameDe: displayName.de,
    displayNameEn: displayName.en,
    descriptionDe: description.de,
    descriptionEn: description.en,
    ranking: model.ranking !== undefined ? String(model.ranking) : ''
  };
}

// ===== Component =====

export function SttModelDetailPage() {
  const { providerId, modelName } = useParams<{ providerId: string; modelName: string }>();
  const navigate = useNavigate();

  const [provider, setProvider] = useState<SpeechToTextProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Basic form
  const [displayNameDe, setDisplayNameDe] = useState('');
  const [displayNameEn, setDisplayNameEn] = useState('');
  const [descriptionDe, setDescriptionDe] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [ranking, setRanking] = useState('');

  // Config editor
  const [configFields, setConfigFields] = useState<ConfigFieldFormData[]>([]);
  const [configJsonText, setConfigJsonText] = useState('{}');
  const [configTab, setConfigTab] = useState<string>('visual');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Export/Import dialogs
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportJson, setExportJson] = useState('');
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<SttModel | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initial data ref for dirty tracking
  const initialDataRef = useRef<string>('');

  const loadProvider = useCallback(async () => {
    if (!providerId) return;
    try {
      setLoading(true);
      const data = await apiService.getSpeechToTextProvider(providerId);
      setProvider(data);
      setError(null);

      const model = data.models.find(m => m.name === modelName);
      if (!model) {
        setError(`Modell "${modelName}" nicht gefunden`);
        return;
      }

      const basic = modelToBasicForm(model);
      setDisplayNameDe(basic.displayNameDe);
      setDisplayNameEn(basic.displayNameEn);
      setDescriptionDe(basic.descriptionDe);
      setDescriptionEn(basic.descriptionEn);
      setRanking(basic.ranking);

      const fields = configSchemaToFields(model.configSchema);
      setConfigFields(fields);
      const schema = model.configSchema || {};
      setConfigJsonText(JSON.stringify(schema, null, 2));

      // Store initial state for dirty tracking
      initialDataRef.current = JSON.stringify({
        ...basic,
        configSchema: schema
      });
      setIsDirty(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden des Providers';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [providerId, modelName]);

  useEffect(() => {
    loadProvider();
  }, [loadProvider]);

  // Dirty tracking
  const checkDirty = useCallback(() => {
    const currentSchema = fieldsToConfigSchema(configFields);
    const currentData = JSON.stringify({
      displayNameDe,
      displayNameEn,
      descriptionDe,
      descriptionEn,
      ranking,
      configSchema: currentSchema
    });
    setIsDirty(currentData !== initialDataRef.current);
  }, [displayNameDe, displayNameEn, descriptionDe, descriptionEn, ranking, configFields]);

  useEffect(() => {
    checkDirty();
  }, [checkDirty]);

  // Sync between visual and JSON tabs
  const handleTabChange = (newTab: string) => {
    if (newTab === 'json' && configTab === 'visual') {
      // Visual -> JSON: serialize fields to JSON
      const schema = fieldsToConfigSchema(configFields);
      setConfigJsonText(JSON.stringify(schema, null, 2));
      setJsonError(null);
    } else if (newTab === 'visual' && configTab === 'json') {
      // JSON -> Visual: parse JSON to fields
      try {
        const parsed = JSON.parse(configJsonText) as SttModelConfigSchema;
        setConfigFields(configSchemaToFields(parsed));
        setJsonError(null);
      } catch (e) {
        setJsonError(`JSON-Fehler: ${e instanceof Error ? e.message : 'Parsefehler'}`);
        return; // Don't switch tab on error
      }
    }
    setConfigTab(newTab);
  };

  // ===== Field management =====

  const updateField = (index: number, updates: Partial<ConfigFieldFormData>) => {
    setConfigFields(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const addField = () => {
    setConfigFields(prev => [...prev, emptyField()]);
  };

  const removeField = (index: number) => {
    setConfigFields(prev => prev.filter((_, i) => i !== index));
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    setConfigFields(prev => {
      const next = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  // ===== Option management =====

  const addOption = (fieldIndex: number) => {
    updateField(fieldIndex, {
      options: [...configFields[fieldIndex].options, emptyOption()]
    });
  };

  const updateOption = (fieldIndex: number, optionIndex: number, updates: Partial<OptionFormData>) => {
    const newOptions = [...configFields[fieldIndex].options];
    newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
    updateField(fieldIndex, { options: newOptions });
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const newOptions = configFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    updateField(fieldIndex, { options: newOptions });
  };

  // ===== Save =====

  const handleSave = async () => {
    if (!provider || !modelName) return;

    // Build updated model
    const displayName: Record<string, string> = {};
    if (displayNameDe) displayName['de'] = displayNameDe;
    if (displayNameEn) displayName['en'] = displayNameEn;

    const description: Record<string, string> = {};
    if (descriptionDe) description['de'] = descriptionDe;
    if (descriptionEn) description['en'] = descriptionEn;

    let configSchema: SttModelConfigSchema;
    if (configTab === 'json') {
      try {
        configSchema = JSON.parse(configJsonText) as SttModelConfigSchema;
      } catch {
        alert('Ungültiges JSON im Config Schema');
        return;
      }
    } else {
      configSchema = fieldsToConfigSchema(configFields);
    }

    const updatedModel: SttModel = {
      name: modelName,
      displayName: Object.keys(displayName).length > 0 ? displayName : modelName,
      ...(Object.keys(description).length > 0 && { description }),
      ...(ranking !== '' && { ranking: Number(ranking) }),
      ...(Object.keys(configSchema).length > 0 && { configSchema })
    };

    const newModels = provider.models.map(m =>
      m.name === modelName ? updatedModel : m
    );

    try {
      setSaving(true);
      await apiService.updateSpeechToTextProvider(provider.id, { models: newModels });
      setProvider({ ...provider, models: newModels });

      // Update initial data ref
      initialDataRef.current = JSON.stringify({
        displayNameDe,
        displayNameEn,
        descriptionDe,
        descriptionEn,
        ranking,
        configSchema
      });
      setIsDirty(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Speichern';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  // ===== Export =====

  const openExportDialog = () => {
    if (!provider) return;
    const model = provider.models.find(m => m.name === modelName);
    if (!model) return;
    setExportJson(JSON.stringify(model, null, 2));
    setIsExportDialogOpen(true);
  };

  const handleCopyExport = async () => {
    const copied = await copyToClipboard(exportJson, 'Export-JSON');
    if (!copied) {
      // Fallback: Text markieren, damit manuell kopiert werden kann.
      const textarea = document.querySelector<HTMLTextAreaElement>('#export-json');
      textarea?.select();
    }
  };

  const handleDownloadExport = () => {
    if (!modelName) return;
    const blob = new Blob([exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${modelName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== Import =====

  const openImportDialog = () => {
    setImportJson('');
    setImportPreview(null);
    setImportError(null);
    setIsImportDialogOpen(true);
  };

  const validateImportJson = (json: string) => {
    setImportJson(json);
    setImportError(null);
    setImportPreview(null);
    if (!json.trim()) return;

    try {
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed)) {
        setImportError('Erwartet ein einzelnes Modell-Objekt, kein Array');
        return;
      }
      if (!parsed.name || typeof parsed.name !== 'string') {
        setImportError('"name" fehlt oder ist ungültig');
        return;
      }
      if (!parsed.displayName) {
        setImportError('"displayName" fehlt');
        return;
      }
      setImportPreview(parsed as SttModel);
    } catch (e) {
      setImportError(`Ungültiges JSON: ${e instanceof Error ? e.message : 'Parsefehler'}`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target?.result;
      if (typeof content === 'string') {
        validateImportJson(content);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportConfirm = () => {
    if (!importPreview) return;

    // Apply imported model data to current form
    const basic = modelToBasicForm(importPreview);
    setDisplayNameDe(basic.displayNameDe);
    setDisplayNameEn(basic.displayNameEn);
    setDescriptionDe(basic.descriptionDe);
    setDescriptionEn(basic.descriptionEn);
    setRanking(basic.ranking);

    const fields = configSchemaToFields(importPreview.configSchema);
    setConfigFields(fields);
    const schema = importPreview.configSchema || {};
    setConfigJsonText(JSON.stringify(schema, null, 2));

    setIsImportDialogOpen(false);
  };

  // ===== Render =====

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12 text-gray-500">Lade Modell...</div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">{error || 'Provider nicht gefunden'}</div>
          <Button onClick={() => navigate(`/speech-to-text/${providerId}/models`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Modellübersicht
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/speech-to-text/${providerId}/models`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getLocalizedText(provider.models.find(m => m.name === modelName)?.displayName) || modelName}
            </h1>
            <p className="text-gray-500 text-sm">
              {provider.displayName} &middot; <code className="text-xs">{modelName}</code>
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={openImportDialog}>
            <Upload className="w-4 h-4 mr-2" />
            Importieren
          </Button>
          <Button variant="outline" size="sm" onClick={openExportDialog}>
            <Download className="w-4 h-4 mr-2" />
            Exportieren
          </Button>
          <Button onClick={handleSave} disabled={saving || !isDirty}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>
      </div>

      {isDirty && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg">
          Es gibt ungespeicherte Änderungen.
        </div>
      )}

      {/* Basic Data */}
      <Card>
        <CardHeader>
          <CardTitle>Grunddaten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name (technisch)</Label>
              <Input value={modelName || ''} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="detail-ranking">Ranking</Label>
              <Input
                id="detail-ranking"
                type="number"
                value={ranking}
                onChange={(e) => setRanking(e.target.value)}
                placeholder="z.B. 1 (niedriger = höhere Priorität)"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="detail-displayname-de">Anzeigename (Deutsch)</Label>
              <Input
                id="detail-displayname-de"
                value={displayNameDe}
                onChange={(e) => setDisplayNameDe(e.target.value)}
                placeholder="z.B. Nova 3"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="detail-displayname-en">Anzeigename (English)</Label>
              <Input
                id="detail-displayname-en"
                value={displayNameEn}
                onChange={(e) => setDisplayNameEn(e.target.value)}
                placeholder="e.g. Nova 3"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="detail-description-de">Beschreibung (Deutsch)</Label>
              <Input
                id="detail-description-de"
                value={descriptionDe}
                onChange={(e) => setDescriptionDe(e.target.value)}
                placeholder="Kurze Beschreibung auf Deutsch"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="detail-description-en">Beschreibung (English)</Label>
              <Input
                id="detail-description-en"
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                placeholder="Short description in English"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Config Schema Editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Konfigurationsschema</CardTitle>
            <Badge variant="secondary">
              {configFields.length} Feld{configFields.length !== 1 ? 'er' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={configTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2 max-w-xs mb-4">
              <TabsTrigger value="visual">Visuell</TabsTrigger>
              <TabsTrigger value="json">JSON</TabsTrigger>
            </TabsList>

            <TabsContent value="visual" className="space-y-4">
              {configFields.map((field, fieldIndex) => (
                <Card key={fieldIndex} className="border-gray-200">
                  <CardContent className="pt-4 space-y-4">
                    {/* Field header row */}
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => moveField(fieldIndex, 'up')}
                          disabled={fieldIndex === 0}
                        >
                          <GripVertical className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Schlüssel</Label>
                          <Input
                            value={field.key}
                            onChange={(e) => updateField(fieldIndex, { key: e.target.value })}
                            placeholder="z.B. language"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Typ</Label>
                          <Select
                            value={field.type}
                            onValueChange={(value) => updateField(fieldIndex, {
                              type: value as SttModelConfigField['type']
                            })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map(ft => (
                                <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs text-gray-500">Gruppe</Label>
                            <Input
                              value={field.group}
                              onChange={(e) => updateField(fieldIndex, { group: e.target.value })}
                              placeholder="optional"
                              className="h-8 text-sm"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 shrink-0"
                            onClick={() => removeField(fieldIndex)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Label & Description */}
                    <div className="grid grid-cols-2 gap-3 pl-8">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Label (DE)</Label>
                        <Input
                          value={field.labelDe}
                          onChange={(e) => updateField(fieldIndex, { labelDe: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Label (EN)</Label>
                        <Input
                          value={field.labelEn}
                          onChange={(e) => updateField(fieldIndex, { labelEn: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Beschreibung (DE)</Label>
                        <Input
                          value={field.descriptionDe}
                          onChange={(e) => updateField(fieldIndex, { descriptionDe: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Beschreibung (EN)</Label>
                        <Input
                          value={field.descriptionEn}
                          onChange={(e) => updateField(fieldIndex, { descriptionEn: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>

                    {/* Default & Order */}
                    <div className="grid grid-cols-3 gap-3 pl-8">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Standardwert</Label>
                        {field.type === 'boolean' ? (
                          <Select
                            value={field.defaultValue || 'false'}
                            onValueChange={(v) => updateField(fieldIndex, { defaultValue: v })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="true">true</SelectItem>
                              <SelectItem value="false">false</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={field.defaultValue}
                            onChange={(e) => updateField(fieldIndex, { defaultValue: e.target.value })}
                            type={field.type === 'number' || field.type === 'slider' ? 'number' : 'text'}
                            className="h-8 text-sm"
                          />
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Reihenfolge</Label>
                        <Input
                          type="number"
                          value={field.order}
                          onChange={(e) => updateField(fieldIndex, { order: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                      {/* Type-specific fields */}
                      {(field.type === 'string' || field.type === 'textarea') && (
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Max. Länge</Label>
                          <Input
                            type="number"
                            value={field.maxLength}
                            onChange={(e) => updateField(fieldIndex, { maxLength: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                      )}
                    </div>

                    {/* Number/Slider: min, max, step */}
                    {(field.type === 'number' || field.type === 'slider') && (
                      <div className="grid grid-cols-3 gap-3 pl-8">
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Min</Label>
                          <Input
                            type="number"
                            value={field.min}
                            onChange={(e) => updateField(fieldIndex, { min: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Max</Label>
                          <Input
                            type="number"
                            value={field.max}
                            onChange={(e) => updateField(fieldIndex, { max: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Schrittweite</Label>
                          <Input
                            type="number"
                            value={field.step}
                            onChange={(e) => updateField(fieldIndex, { step: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {/* Select: Options */}
                    {field.type === 'select' && (
                      <div className="pl-8 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-500 font-medium">
                            Optionen ({field.options.length})
                          </Label>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => addOption(fieldIndex)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Option
                          </Button>
                        </div>
                        {field.options.map((option, optIndex) => (
                          <div key={optIndex} className="grid grid-cols-6 gap-2 items-end">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Wert</Label>
                              <Input
                                value={option.value}
                                onChange={(e) => updateOption(fieldIndex, optIndex, { value: e.target.value })}
                                className="h-7 text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Label DE</Label>
                              <Input
                                value={option.labelDe}
                                onChange={(e) => updateOption(fieldIndex, optIndex, { labelDe: e.target.value })}
                                className="h-7 text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Label EN</Label>
                              <Input
                                value={option.labelEn}
                                onChange={(e) => updateOption(fieldIndex, optIndex, { labelEn: e.target.value })}
                                className="h-7 text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Hinweis DE</Label>
                              <Input
                                value={option.hintDe}
                                onChange={(e) => updateOption(fieldIndex, optIndex, { hintDe: e.target.value })}
                                className="h-7 text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Gruppe</Label>
                              <Input
                                value={option.group}
                                onChange={(e) => updateOption(fieldIndex, optIndex, { group: e.target.value })}
                                className="h-7 text-xs"
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              onClick={() => removeOption(fieldIndex, optIndex)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              <Button variant="outline" onClick={addField} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Feld hinzufügen
              </Button>
            </TabsContent>

            <TabsContent value="json">
              {jsonError && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg mb-3">
                  {jsonError}
                </div>
              )}
              <Textarea
                value={configJsonText}
                onChange={(e) => {
                  setConfigJsonText(e.target.value);
                  setJsonError(null);
                }}
                className="min-h-64 font-mono text-xs"
                placeholder='{"language": {"type": "select", ...}}'
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modell exportieren</DialogTitle>
            <DialogDescription>
              Modell "{modelName}" als JSON exportieren
            </DialogDescription>
          </DialogHeader>

          <Textarea
            id="export-json"
            value={exportJson}
            readOnly
            className="min-h-64 font-mono text-xs"
          />

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleCopyExport}>
              <Copy className="w-4 h-4 mr-2" />
              Kopieren
            </Button>
            <Button onClick={handleDownloadExport}>
              <Download className="w-4 h-4 mr-2" />
              Herunterladen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modell importieren</DialogTitle>
            <DialogDescription>
              Importieren Sie ein Modell als JSON. Die aktuellen Formulardaten werden überschrieben.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="paste">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="paste">JSON einfügen</TabsTrigger>
              <TabsTrigger value="file">Datei hochladen</TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="space-y-3">
              <Textarea
                value={importJson}
                onChange={(e) => validateImportJson(e.target.value)}
                placeholder='{"name": "nova-3", "displayName": {"de": "Nova 3"}, ...}'
                className="min-h-48 font-mono text-xs"
              />
            </TabsContent>

            <TabsContent value="file" className="space-y-3">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">JSON-Datei auswählen</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Datei auswählen
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </TabsContent>
          </Tabs>

          {importError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {importError}
            </div>
          )}

          {importPreview && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Vorschau</h4>
              <div className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-sm">
                      {getLocalizedText(importPreview.displayName)}
                    </span>
                    <code className="text-xs text-gray-500 ml-2">{importPreview.name}</code>
                  </div>
                  {importPreview.configSchema && (
                    <Badge variant="secondary">
                      {Object.keys(importPreview.configSchema).length} Felder
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleImportConfirm} disabled={!importPreview}>
              Übernehmen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
